import {
  doc, getDoc, serverTimestamp, setDoc, Timestamp,
} from 'firebase/firestore';
import { auth, db } from '../../services/firebase';

const cleanPriorities = (items = []) => items.map((item, index) => ({
  id: String(item?.id || `priority-${index + 1}`).trim(),
  text: String(item?.text || '').trim(),
  sourceIds: Array.isArray(item?.sourceIds) ? item.sourceIds.filter(Boolean) : [],
})).filter((item) => item.text);

const cleanMeetingBrief = (brief) => {
  if (!brief || typeof brief !== 'object') return null;
  return {
    templateId: String(brief.templateId || '').trim(),
    templateVersion: Number(brief.templateVersion) || null,
    reviewedPeriodText: String(brief.reviewedPeriodText || '').trim(),
    sections: (Array.isArray(brief.sections) ? brief.sections : []).map((section) => ({
      id: String(section?.id || '').trim(),
      title: String(section?.title || '').trim(),
      statements: (Array.isArray(section?.statements) ? section.statements : []).map((statement) => ({
        id: String(statement?.id || '').trim(),
        text: String(statement?.text || '').trim(),
        sourceIds: Array.isArray(statement?.sourceIds) ? statement.sourceIds.filter(Boolean) : [],
        provenance: String(statement?.provenance || '').trim(),
      })).filter((statement) => statement.id && statement.text),
    })).filter((section) => section.id && section.title && section.statements.length),
  };
};

const cleanBriefHeader = (header) => {
  if (!header || typeof header !== 'object') return null;
  return {
    title: String(header.title || '').trim(),
    purpose: String(header.purpose || '').trim(),
  };
};

const cleanAnswers = (answers = []) => answers.map((answer) => ({
  questionId: String(answer?.questionId || '').trim(),
  proposedText: String(answer?.proposedText || '').trim(),
  candidatePoints: (Array.isArray(answer?.candidatePoints) ? answer.candidatePoints : []).map((point) => ({
    text: String(point?.text || '').trim(),
    sourceIds: Array.isArray(point?.sourceIds) ? point.sourceIds.filter(Boolean) : [],
  })).filter((point) => point.text),
  confirmedText: String(answer?.confirmedText || '').trim(),
  status: String(answer?.status || 'unconfirmed').trim(),
  origin: String(answer?.origin || 'ai_suggested').trim(),
  sourceIds: Array.isArray(answer?.sourceIds) ? answer.sourceIds.filter(Boolean) : [],
  evidenceSourceIds: Array.isArray(answer?.evidenceSourceIds) ? answer.evidenceSourceIds.filter(Boolean) : [],
  evidenceFingerprint: String(answer?.evidenceFingerprint || '').trim() || null,
  generatedAt: answer?.generatedAt || null,
  confirmedAt: answer?.confirmedAt || null,
  skippedAt: answer?.skippedAt || null,
  updatedAt: answer?.updatedAt || null,
  updatedBy: String(answer?.updatedBy || '').trim(),
})).filter((answer) => answer.questionId);

const requireDraftIdentity = ({ draftId, journeyType, templateId, templateVersion }, { requireJourneyType = true } = {}) => {
  if (!String(draftId || '').trim()) throw new Error('Journey draftId is required.');
  if (requireJourneyType && !String(journeyType || '').trim()) throw new Error('Journey journeyType is required.');
  if (!String(templateId || '').trim()) throw new Error('Journey templateId is required.');
  if (!Number.isInteger(templateVersion) || templateVersion < 1) {
    throw new Error('Journey templateVersion must be a positive integer.');
  }
};

export const getPreparationDraft = async ({ childId, draftId }) => {
  if (!childId) return null;
  if (!String(draftId || '').trim()) throw new Error('Journey draftId is required.');
  const snapshot = await getDoc(doc(db, 'children', childId, 'preparationDrafts', draftId));
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
};

const updatePreparationArchiveState = async ({ childId, draftId, isArchived }) => {
  if (!childId || !String(draftId || '').trim()) throw new Error('Preparation is required.');
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Please sign in before updating this preparation.');
  const ref = doc(db, 'children', childId, 'preparationDrafts', draftId);
  const existing = await getDoc(ref);
  if (!existing.exists()) throw new Error('This preparation could not be found.');
  const payload = isArchived ? {
    isArchived: true,
    archivedAt: serverTimestamp(),
    archivedBy: uid,
  } : {
    isArchived: false,
    restoredAt: serverTimestamp(),
    restoredBy: uid,
  };
  await setDoc(ref, payload, { merge: true });
  return payload;
};

export const archivePreparationDraft = (identity) => updatePreparationArchiveState({
  ...identity, isArchived: true,
});

export const restorePreparationDraft = (identity) => updatePreparationArchiveState({
  ...identity, isArchived: false,
});

export const savePreparationDraft = async ({
  childId,
  draftId,
  journeyType,
  templateId,
  templateVersion,
  title,
  meetingDate,
  previousIepDate,
  handoverPeriod,
  confirmedPriorities,
  userNotes,
  suggestionDecisions,
  evidenceDecisions,
  meetingBrief,
  briefHeader,
  answers,
}) => {
  if (!childId) throw new Error('Choose a person before saving this preparation.');
  requireDraftIdentity({ draftId, journeyType, templateId, templateVersion });
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Please sign in before saving this preparation.');
  const ref = doc(db, 'children', childId, 'preparationDrafts', draftId);
  const existing = await getDoc(ref);
  if (existing.exists()) {
    const existingData = existing.data() || {};
    if (existingData.templateId !== templateId
      || Number(existingData.templateVersion) !== Number(templateVersion)
      || existingData.journeyType !== journeyType) {
      throw new Error('This preparation uses a different Journey template version.');
    }
  }
  const payload = {
    childId,
    journeyType,
    templateId,
    templateVersion,
    updatedAt: serverTimestamp(),
    updatedBy: uid,
  };
  if (title !== undefined) payload.title = String(title || '').trim();
  if (meetingDate !== undefined) payload.meetingDate = meetingDate || null;
  if (handoverPeriod !== undefined) payload.handoverPeriod = handoverPeriod;
  if (previousIepDate !== undefined) payload.previousIepDate = previousIepDate || null;
  if (confirmedPriorities !== undefined) payload.confirmedPriorities = cleanPriorities(confirmedPriorities);
  if (userNotes !== undefined) payload.userNotes = String(userNotes || '').trim();
  if (suggestionDecisions !== undefined) payload.suggestionDecisions = suggestionDecisions;
  if (evidenceDecisions !== undefined) payload.evidenceDecisions = evidenceDecisions;
  if (meetingBrief !== undefined) payload.meetingBrief = cleanMeetingBrief(meetingBrief);
  if (briefHeader !== undefined) payload.briefHeader = cleanBriefHeader(briefHeader);
  if (answers !== undefined) payload.answers = cleanAnswers(answers);
  if (!existing.exists()) {
    if (confirmedPriorities === undefined) payload.confirmedPriorities = [];
    payload.createdAt = serverTimestamp();
    payload.createdBy = uid;
  }
  await setDoc(ref, payload, { merge: true });
  return payload;
};

export const makePreparationDecision = ({ status, ...fields }) => ({
  ...fields,
  status,
  decidedAt: Timestamp.now(),
  decidedBy: auth.currentUser?.uid || '',
});

export const makePreparationAnswer = ({ status, origin, ...fields }) => {
  const now = Timestamp.now();
  return {
    ...fields,
    status,
    origin,
    ...(status === 'confirmed' ? { confirmedAt: now } : {}),
    ...(status === 'skipped' ? { skippedAt: now } : {}),
    updatedAt: now,
    updatedBy: auth.currentUser?.uid || '',
  };
};

export { cleanAnswers, cleanBriefHeader, cleanMeetingBrief, cleanPriorities, requireDraftIdentity };
